import '@wangeditor/editor/dist/css/style.css' // 引入 css
import {
  IDomEditor,
  IEditorConfig,
  IToolbarConfig,
  Boot,
} from '@wangeditor/editor'
import { Editor, Toolbar } from '@wangeditor/editor-for-react'
import attachmentModule from '@wangeditor/plugin-upload-attachment'
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'
import styled from 'styled-components'

import { FileDirName } from 'packages/sdks-next/growp'
import { getTheme } from 'packages/theme'
import { uploadFile } from 'packages/utils/file'

import { formulaModule } from '../FormulaPlugin/Formula'

import {
  InsertFnType,
  RichTextEditorProps,
  RichTextEditorRef,
  justifySvg,
  uploadSvg,
} from './types'

Boot.registerModule(formulaModule)
Boot.registerModule(attachmentModule)
/**
 * 富文本编辑器
 *
 * @returns richtext editor
 */
export const RichTextEditor = forwardRef<
  RichTextEditorRef,
  RichTextEditorProps
>(
  (
    {
      onChange,
      value,
      minHeight = 300,
      readOnly = false,
      placeholder = '请输入',
      autoFocus = false,
      useFullScreen = true,
      insertKeys,
      excludeKeys = [],
      onEditorBlur,
      onBlur,
      styles,
    },
    ref
  ) => {
    const [editorValue, setEditorValue] = useState<string>(value || '')
    // editor 实例
    const [editor, setEditor] = useState<IDomEditor | null>(null)
    const theme = getTheme()
    // 工具栏配置
    const toolbarConfig: Partial<IToolbarConfig> = {
      insertKeys,
      toolbarKeys: [
        'headerSelect',
        '|',
        'bold',
        'italic',
        'underline',
        'through',
        'color',
        'bgColor',
        '|',
        {
          key: 'group-justify',
          title: '对齐',
          iconSvg: justifySvg,
          menuKeys: [
            'justifyLeft',
            'justifyRight',
            'justifyCenter',
            'justifyJustify',
          ],
        },
        'indent',
        'delIndent',
        'numberedList',
        'bulletedList',
        '|',
        'insertTable',
        {
          key: 'group-upload',
          title: '上传',
          iconSvg: uploadSvg,
          menuKeys: ['uploadImage', 'uploadVideo', 'uploadAttachment'],
        },
        'insertLink',
        'fullScreen',
      ],
      excludeKeys: [useFullScreen ? '' : 'fullScreen', ...excludeKeys],
      // modalAppendToBody: true,
    }

    useEffect(() => {
      onChange && onChange(editorValue)
    }, [editorValue])

    // 编辑器配置
    const editorConfig: Partial<IEditorConfig> = {
      onBlur,
      readOnly,
      placeholder,
      autoFocus,
      hoverbarKeys: {
        attachment: {
          menuKeys: ['downloadAttachment'], // “下载附件”菜单
        },
        formula: {
          menuKeys: ['editFormula'],
        },
      },
      MENU_CONF: {
        uploadImage: {
          server: 'https://seiue-chalkng-staging.oss-cn-qingdao.aliyuncs.com',
          async customUpload(file: File, insertFn: InsertFnType) {
            const { data: ossData } = await uploadFile({
              file,
              dirname: FileDirName.Images,
            })
            if (!ossData?.url) throw new Error(`url is empty`)

            insertFn(ossData?.url || '', file.name, ossData?.url || '')
          },
        },
        uploadVideo: {
          server: 'https://seiue-chalkng-staging.oss-cn-qingdao.aliyuncs.com',
          async customUpload(file: File, insertFn: InsertFnType) {
            const { data: ossData } = await uploadFile({
              file,
              dirname: FileDirName.Video,
            })

            if (!ossData?.url) throw new Error(`url is empty`)
            insertFn(ossData?.url || '', file.name, ossData?.url || '')
          },
        },
        uploadAttachment: {
          server: 'https://seiue-chalkng-staging.oss-cn-qingdao.aliyuncs.com',
          customUpload: async (file: File, insertFn: Function) => {
            const { data: ossData } = await uploadFile({
              file,
              dirname: FileDirName.File,
            })

            if (!ossData?.url) throw new Error(`url is empty`)
            insertFn(file.name, ossData?.url || '')
          },
        },
      },
    }

    // 及时销毁 editor ，重要！
    useEffect(() => {
      return () => {
        if (editor == null) return
        editor.destroy()
        setEditor(null)
      }
    }, [editor])

    useImperativeHandle(ref, () => ({
      getPlainText: () => {
        return editor?.getText()
      },
    }))

    return (
      <EditorWrapper
        tabIndex={1}
        onClick={() => {
          if (!editor?.isFocused()) {
            editor?.focus()
          }
        }}
        onBlur={onEditorBlur}
        $minHeight={minHeight}
      >
        <Toolbar
          editor={editor}
          defaultConfig={toolbarConfig}
          mode="default"
          style={{
            borderBottom: `1px solid ${theme.border.greyNormal}`,
          }}
        />
        <Editor
          defaultConfig={editorConfig}
          value={editorValue}
          onCreated={setEditor}
          onChange={(editor) => {
            setEditorValue(
              editor.getHtml() === '<p><br></p>' ? '' : editor.getHtml()
            )
          }}
          style={{
            height: 'auto',
            overflowY: 'hidden',
            minHeight,
            backgroundColor: theme.background._3,
            borderRadius: '0px 0px 8px 8px',
            ...styles,
          }}
          mode="default"
        />
      </EditorWrapper>
    )
  }
)

const EditorWrapper = styled.div<{ $minHeight?: number }>`
  z-index: 9999;
  width: 100%;
  cursor: text;
  &&& {
    .w-e-bar {
      display: flex;
      align-items: center;
      gap: 4px;
      svg {
        fill: ${(props) => props.theme.text._2};
        font-weight: 400;
      }
    }
    .w-e-bar-item-group .w-e-bar-item-menus-container {
      border-radius: 6px;
    }
    .w-e-select-list {
      border-radius: 6px;
      & ul .selected,
      & ul li:hover {
        background-color: ${(props) => props.theme.background._3};
      }
    }
    .w-e-bar-item {
      padding: 0 0;
      min-width: 24px;
      min-height: 24px;
      .disabled,
      .disabled svg {
        color: ${(props) => props.theme.text._4};
        fill: ${(props) => props.theme.text._4};
      }
      .active {
        background-color: ${(props) => props.theme.background._2};
      }
      button {
        padding: 4 4;
        min-width: 24px;
        min-height: 24px;
        border-radius: 6px;
        color: ${(props) => props.theme.text._2};
        cursor: pointer;
        &:hover {
          background-color: ${(props) => props.theme.background._3};
        }
        &:focus {
          background-color: ${(props) => props.theme.background._2};
        }
      }
    }
    .w-e-bar-item-menus-container {
      z-index: 2000 !important;
    }
    .w-e-toolbar {
      border-radius: 8px 8px 0px 0px;
      background-color: #f5fafa;
      box-shadow: inset 0px -1px 0px 0px #e5e6e8;
    }
    div[data-w-e-toolbar='true'] {
      border-bottom: none !important;
    }
    .w-e-bar-divider {
      height: 16px;
    }
    .w-e-scroll {
      background-color: ${(props) => props.theme.background._3};
      ${({ $minHeight }) => $minHeight && `min-height: ${$minHeight}px;`}
    }
    .w-e-modal {
      position: fixed;
    }
    img {
      max-width: 100%;
    }
    .w-e-textarea-video-container {
      padding: 0 0;
      background: transparent;
    }
    video {
      max-width: 100%;
      max-height: 100%;
    }
  }
`
